/**
 * SearchableSelect — Radix Select with embedded search input.
 * Follows the same pattern as the communities LocationSelect component.
 */

import { useState, useMemo } from 'react';
import { Check, Search } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Input } from '@/core/components/ui/input';

export interface SearchableSelectOption {
  id: string;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  emptyMessage = 'No results found',
  isLoading = false,
  loadingMessage = 'Loading…',
  disabled = false,
  className,
}: SearchableSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.subLabel?.toLowerCase().includes(q) || o.id.includes(q),
    );
  }, [options, searchQuery]);

  const selectedLabel = useMemo(() => {
    if (!value) return undefined;
    return options.find((o) => o.id === value)?.label;
  }, [value, options]);

  const handleOpenChange = (open: boolean) => {
    if (!open) setSearchQuery('');
  };

  return (
    <Select value={value} onValueChange={onValueChange} onOpenChange={handleOpenChange}>
      <SelectTrigger className={cn('w-full', className)} disabled={disabled || isLoading}>
        <SelectValue placeholder={isLoading ? loadingMessage : placeholder}>
          {isLoading ? loadingMessage : selectedLabel ?? placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-[--radix-select-trigger-width] max-h-64">
        {/* Search Input */}
        <div className="flex items-center gap-2 px-2 pb-2 border-b sticky top-0 bg-white z-10">
          <Search className="h-4 w-4 text-gray-400 shrink-0" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>

        {/* Options */}
        {filtered.length > 0 ? (
          filtered.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              <div className="flex items-center justify-between w-full gap-3">
                <div className="flex flex-col min-w-0">
                  <span className="font-medium truncate">{option.label}</span>
                  {option.subLabel && (
                    <span className="text-xs text-gray-500">{option.subLabel}</span>
                  )}
                </div>
                {value === option.id && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </div>
            </SelectItem>
          ))
        ) : (
          <div className="px-2 py-4 text-center text-sm text-gray-500">
            {emptyMessage}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
